/**
 * @file DivertOrderModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-06
 * @description The Divert window: the WHOLE order goes to another customer
 *              (when it cannot be delivered as planned — the customer has no
 *              room). Unlike Split Order, nothing new is made: the SO-D and its
 *              PO numbers stay, still linked, and the customer details change
 *              on all of them (services/orderDivertService).
 *
 *              Sale prices are KEPT unless changed here. Each line shows its
 *              price beside the new customer's contract price and what they
 *              last paid (the order's suggested prices, asked for the new
 *              customer). Choosing the contract price links that contract; a
 *              line priced from the old customer's contract loses that link.
 *              A reason is required.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import './OrderHub.css';

const getJson = async (url) => {
  const res = await fetch(url, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

const round4 = (v) => Math.round(Number(v) * 10000) / 10000;

const isoDay = (d) => (d ? String(d).slice(0, 10) : null);

/**
 * Is the contract in force on the day? Its own dates, else its period's — the
 * server's rule (contractResolution.coversDay), which refuses any other.
 */
function coversDay(c, day) {
  if (!day) return false;
  const start = isoDay(c.start_date);
  const end = isoDay(c.end_date);
  if (start || end) return (!start || start <= day) && (!end || end >= day);
  const ps = isoDay(c.period_start);
  const pe = isoDay(c.period_end);
  return !!(ps || pe) && (!ps || ps <= day) && (!pe || pe >= day);
}

/**
 * The new customer's offers for one line: their contract in force on the
 * delivery date (not merely the newest), and what they last paid.
 */
function offersFor(suggested, deliveryDate) {
  if (!suggested) return [];
  const priceOf = (e) => (e.price_with_haulage != null ? e.price_with_haulage : e.price);
  const out = [];
  const day = isoDay(deliveryDate);
  const contract = (suggested.contracts || []).find(c => Number(c.price) > 0 && coversDay(c, day));
  if (contract) {
    const linkable = contract.contract_id && ['contract', 'monthly'].includes(contract.source_kind);
    out.push({
      key: 'contract',
      price: round4(priceOf(contract)),
      label: `Contract${contract.contract_number ? ` ${contract.contract_number}` : contract.period_name ? ` ${contract.period_name}` : ''}`,
      contract: linkable
        ? { kind: contract.source_kind, id: contract.contract_id, label: contract.contract_number || contract.period_name || 'contract' }
        : null
    });
  }
  const last = (suggested.previously_sold || []).find(p => Number(p.price) > 0);
  if (last) {
    out.push({ key: 'last', price: round4(priceOf(last)), label: `Last sold${last.doc_number ? ` (${last.doc_number})` : ''}`, contract: null });
  }
  return out;
}

/** Rendered as the Modal's body so useSlideOutButton mounts with the footer's buttons. */
function DivertBody({
  loading, order, customers, accountId, setAccountId, suggestions, newCustomer, sharedPos, choices, setChoice,
  reason, setReason, error, saving
}) {
  useSlideOutButton([loading, saving, accountId]);

  if (loading) return <LoadingSpinner />;
  if (!order) return <p className="divert-error">{error || 'Order not found'}</p>;

  const lines = order.lines || [];
  const oldName = order.customer_details?.company_name || order.company_name || 'the current customer';
  const pricedFromContract = lines.some(l => l.contract_id || l.monthly_pricing_id);
  const directDelivery = order.supplier_delivery_terms === 'Direct Delivery';
  const thirdParty = order.excel_agri_delivery_terms === 'Third-Party Delivery';

  return (
    <div className="divert-body">
      <p className="divert-intro">
        Send the whole of {order.order_number} to another customer. The order and its purchase orders keep
        their numbers and stay linked; the customer, delivery address and rep change on all of them.
        {' '}To send only part of it, use Split Order instead.
      </p>

      <div className="divert-field">
        <label>Divert to <span className="required">*</span></label>
        <SearchableDropdown
          options={customers}
          value={accountId}
          onChange={(val) => setAccountId(val)}
          placeholder="Select customer..."
        />
      </div>

      {accountId && (
        <div className="divert-notices">
          {newCustomer?.credit_status === 'over_limit' && (
            <div className="divert-notice warn">
              <span className="material-symbols-rounded">warning</span>
              <span>
                {newCustomer.company_name} is over its credit limit — {formatPrice(newCustomer.outstanding_balance)} outstanding
                against {formatPrice(newCustomer.credit_limit)}.
              </span>
            </div>
          )}
          {(sharedPos || []).length > 0 && (
            <div className="divert-notice warn">
              <span className="material-symbols-rounded">call_split</span>
              <span>
                {sharedPos.map(p => `${p.purchase_order_number}${p.direct_delivery ? ' (Direct Delivery — the supplier will still deliver to the old address)' : ''}`).join(', ')}
                {' '}also {sharedPos.length === 1 ? 'supplies' : 'supply'} {[...new Set(sharedPos.flatMap(p => p.other_orders || []))].join(', ')},
                {' '}so {sharedPos.length === 1 ? 'it keeps' : 'they keep'} the current customer, delivery address, contract numbers and haulage.
                {' '}Sort {sharedPos.length === 1 ? 'it' : 'them'} out with the supplier separately.
              </span>
            </div>
          )}
          {pricedFromContract && (
            <div className="divert-notice warn">
              <span className="material-symbols-rounded">link_off</span>
              <span>
                Lines priced from {oldName}'s contracts lose that link (the price is kept), unless you choose the new
                customer's contract price below, which links theirs.
              </span>
            </div>
          )}
          {directDelivery && (
            <div className="divert-notice">
              <span className="material-symbols-rounded">local_shipping</span>
              <span>Direct Delivery: the supplier's haulage is re-rated for the new customer's postcode (a typed rate is kept).</span>
            </div>
          )}
          {thirdParty && (
            <div className="divert-notice">
              <span className="material-symbols-rounded">local_shipping</span>
              <span>The haulier's PO-H takes the new delivery address.</span>
            </div>
          )}
          {order.notes && (
            <div className="divert-notice">
              <span className="material-symbols-rounded">sticky_note_2</span>
              <span>The order's delivery instructions are kept ("{order.notes}") — edit the order afterwards if they were for {oldName}.</span>
            </div>
          )}
        </div>
      )}

      {accountId && (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Current price</th>
                <th>New customer's prices</th>
                <th>Price after diverting</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(line => {
                const choice = choices[line.id] || {};
                const offers = offersFor(suggestions?.[line.id], order.delivery_date);
                const current = round4(line.unit_price || 0);
                const changed = choice.price !== '' && round4(choice.price) !== current;
                return (
                  <tr key={line.id}>
                    <td>
                      {line.product_name || line.product_code}
                      {(line.contract_id || line.monthly_pricing_id) && !choice.contract && (
                        <span className="line-note">Priced from {oldName}'s contract — link cleared</span>
                      )}
                    </td>
                    <td>{formatQuantity(line.quantity)}</td>
                    <td className="price-cell">{formatPrice(current)}</td>
                    <td>
                      {suggestions === null
                        ? <span className="muted">Loading…</span>
                        : offers.length === 0
                          ? <span className="muted">No contract or previous price</span>
                          : (
                            <div className="suggestion-chips">
                              {offers.map(o => (
                                <button
                                  key={o.key}
                                  type="button"
                                  className={`suggestion-chip${round4(choice.price) === o.price && (!o.contract || choice.contract?.id === o.contract.id) ? ' chosen' : ''}`}
                                  title={o.contract ? `Use ${formatPrice(o.price)} and link ${o.contract.label}` : `Use ${formatPrice(o.price)}`}
                                  onClick={() => setChoice(line.id, { price: String(o.price), contract: o.contract })}
                                >
                                  {o.label} {formatPrice(o.price)}
                                </button>
                              ))}
                            </div>
                          )}
                    </td>
                    <td className="price-cell">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="price-input"
                        value={choice.price ?? ''}
                        onChange={(e) => setChoice(line.id, { price: e.target.value, contract: null })}
                      />
                      {(changed || choice.contract) && (
                        <button
                          type="button"
                          className="suggestion-chip"
                          title="Keep the current price"
                          onClick={() => setChoice(line.id, { price: String(current), contract: null })}
                        >
                          Keep
                        </button>
                      )}
                      {choice.contract && <span className="line-note">Links {choice.contract.label}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="divert-field">
        <label>Reason <span className="required">*</span></label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Why is it being diverted? e.g. customer has no room — shed full"
        />
      </div>

      {error && <p className="divert-error">{error}</p>}
    </div>
  );
}

/**
 * @param {boolean}  isOpen
 * @param {number}   orderId      The customer order (the hub) to divert.
 * @param {function} onClose
 * @param {function} onDiverted   (result) — the divert's response (po_changes, haulage_refreshed …)
 */
function DivertOrderModal({ isOpen, orderId, onClose, onDiverted }) {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [newCustomer, setNewCustomer] = useState(null);
  // POs a divert to this customer would leave alone (shared with another customer's order).
  const [sharedPos, setSharedPos] = useState([]);
  const [choices, setChoices] = useState({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, c] = await Promise.all([
        getJson(`${API_URL}/api/orders/customer-orders/${orderId}`),
        getJson(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=CUSTOMER&limit=10000`)
      ]);
      setOrder(o);
      setCustomers((c.contacts || [])
        .filter(x => Number(x.id) !== Number(o.account_id))
        .map(x => ({ value: String(x.id), label: `${x.company_name}${x.account_no ? ` (${x.account_no})` : ''}` })));
      setChoices(Object.fromEntries((o.lines || []).map(l => [l.id, { price: String(round4(l.unit_price || 0)), contract: null }])));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (isOpen && orderId) {
      setAccountId('');
      setReason('');
      setSuggestions(null);
      setNewCustomer(null);
      load();
    }
  }, [isOpen, orderId, load]);

  // The new customer's prices and credit position, whenever they change.
  useEffect(() => {
    if (!accountId || !orderId) return undefined;
    let live = true;
    setSuggestions(null);
    setNewCustomer(null);
    setSharedPos([]);
    setChoices(prev => Object.fromEntries(Object.entries(prev).map(([id]) => {
      const line = (order?.lines || []).find(l => String(l.id) === String(id));
      return [id, { price: String(round4(line?.unit_price || 0)), contract: null }];
    })));
    Promise.all([
      getJson(`${API_URL}/api/orders/customer-orders/${orderId}/suggested-prices?accountId=${accountId}`)
        .then(d => d.suggested_prices || {}).catch(() => ({})),
      getJson(`${API_URL}/api/orders/customer-details/${accountId}`).catch(() => null),
      getJson(`${API_URL}/api/orders/customer-orders/${orderId}/divert-preview?accountId=${accountId}`)
        .then(d => d.shared_pos || []).catch(() => [])
    ]).then(([s, cust, shared]) => {
      if (!live) return;
      setSuggestions(s);
      setNewCustomer(cust);
      setSharedPos(shared);
    });
    return () => { live = false; };
  }, [accountId, orderId, order]);

  const setChoice = (lineId, value) => setChoices(prev => ({ ...prev, [lineId]: value }));

  const priceChanges = useMemo(() => (order?.lines || []).flatMap(line => {
    const c = choices[line.id];
    if (!c) return [];
    const price = c.price === '' ? NaN : Number(c.price);
    if (round4(price) === round4(line.unit_price || 0) && !c.contract) return [];
    return [{ line_id: line.id, unit_price: price, contract: c.contract ? { kind: c.contract.kind, id: c.contract.id } : null }];
  }), [order, choices]);

  const submit = async () => {
    setError(null);
    if (!accountId) { setError('Choose the customer the order is going to.'); return; }
    if (!reason.trim()) { setError('Say why the order is being diverted.'); return; }
    if (priceChanges.some(p => !Number.isFinite(p.unit_price) || p.unit_price < 0)) {
      setError('Every price must be a number, zero or more.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/divert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ account_id: Number(accountId), reason: reason.trim(), price_changes: priceChanges })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Divert failed (${res.status})`);
      onDiverted && onDiverted(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div>
        <button data-read-safe className="btn-slideout" onClick={onClose} disabled={saving}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button
          data-write-action
          className="btn-slideout"
          onClick={submit}
          disabled={saving || loading || !order || !accountId || !reason.trim()}
          title={!accountId ? 'Choose the new customer first' : !reason.trim() ? 'A reason is required' : 'Divert the order'}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">alt_route</span></div>
          <span className="btn-label">{saving ? 'Diverting...' : 'Divert'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={order ? `Divert ${order.order_number}` : 'Divert Order'}
      size="xl"
      footer={footer}
      className="divert-order"
    >
      <DivertBody
        loading={loading}
        order={order}
        customers={customers}
        accountId={accountId}
        setAccountId={setAccountId}
        suggestions={suggestions}
        newCustomer={newCustomer}
        sharedPos={sharedPos}
        choices={choices}
        setChoice={setChoice}
        reason={reason}
        setReason={setReason}
        error={error}
        saving={saving}
      />
    </Modal>
  );
}

export default DivertOrderModal;
